import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { LuUnlink, LuLink } from 'react-icons/lu';
import { PiTextTBold, PiTextTSlashBold } from 'react-icons/pi';
import { isSpectrum1D, resizeRange } from '../../../data/data1d/Spectrum1D';
import { isRangeAssigned } from '../../../data/data1d/Spectrum1D/isRangeAssigned';
import { checkRangeKind } from '../../../data/utilities/RangeUtilities';
import { useAssignment, useAssignmentData, } from '../../assignment/AssignmentsContext';
import { filterForIDsWithAssignment } from '../../assignment/utilities/filterForIDsWithAssignment';
import { useDispatch } from '../../context/DispatchContext';
import { useLogger } from '../../context/LoggerContext';
import { useShareData } from '../../context/ShareDataContext';
import { ActionsButtonsPopover, } from '../../elements/ActionsButtonsPopover';
import { ResizerWithScale } from '../../elements/ResizerWithScale';
import { HighlightEventSource, useHighlight } from '../../highlight';
import { useActiveSpectrumRangesViewState } from '../../hooks/useActiveSpectrumRangesViewState';
import { useResizerStatus } from '../../hooks/useResizerStatus';
import useSpectrum from '../../hooks/useSpectrum';
import { options } from '../../toolbar/ToolTypes';
import { IntegralIndicator } from '../integral/IntegralIndicator';
import { useScaleX } from '../utilities/scale';
import { AssignmentLabel } from './AssignmentLabel';
import { Atoms } from './Atoms';
const style = css `
  .target {
    visibility: hidden;
  }

  &:hover {
    .target {
      visibility: visible;
    }
  }
`;
function Range({ range, selectedTool, relativeFormat }) {
    const { id, integration, signals, from, to, diaIDs: rangeDiaIDs, assignment, } = range;
    const assignmentData = useAssignmentData();
    const assignmentRange = useAssignment(id);
    const highlightRange = useHighlight([assignmentRange.id].concat(assignmentRange.assigned?.x || []).concat(filterForIDsWithAssignment(assignmentData, signals.map((_signal) => _signal.id))), { type: HighlightEventSource.RANGE, extra: { id } });
    const scaleX = useScaleX();
    const dispatch = useDispatch();
    const { logger } = useLogger();
    const spectrum = useSpectrum();
    const { showIntegralsValues } = useActiveSpectrumRangesViewState();
    const isBlockedByEditing = selectedTool && selectedTool === options.editRange.id;
    function handleOnStopResizing(position) {
        if (!spectrum || !isSpectrum1D(spectrum))
            return;
        const from = scaleX().invert(position.x2);
        const to = scaleX().invert(position.x1);
        const newRange = resizeRange(spectrum, { from, to, range, logger });
        if (!newRange)
            return;
        dispatch({
            type: 'RESIZE_RANGE',
            payload: {
                range: newRange,
            },
        });
    }
    function mouseEnterHandler() {
        assignmentRange.show('x');
        highlightRange.show();
    }
    function mouseLeaveHandler() {
        assignmentRange.hide();
        highlightRange.hide();
    }
    function assignHandler() {
        if (!isBlockedByEditing) {
            assignmentRange.setActive('x');
        }
    }
    function unAssignHandler(signalIndex = -1) {
        dispatch({
            type: 'UNLINK_RANGE',
            payload: {
                range,
                assignmentData,
                signalIndex,
            },
        });
    }
    const isNotSignal = !checkRangeKind(range);
    const isHighlighted = isBlockedByEditing || highlightRange.isActive || assignmentRange.isActive;
    const isAssigned = isRangeAssigned(range);
    const isResizingActive = useResizerStatus('rangePicking');
    const { setData: addNewAssignmentLabel } = useShareData();
    const isAssignmentActive = !!(assignmentRange.isActive || rangeDiaIDs);
    function removeAssignemntlabel() {
        dispatch({
            type: 'CHANGE_RANGE_ASSIGNMENT_LABEL',
            payload: {
                value: '',
                rangeID: id,
            },
        });
    }
    const actionsButtons = [
        {
            icon: _jsx(LuLink, {}),
            onClick: assignHandler,
            intent: 'success',
            title: 'Assign range',
        },
        {
            icon: _jsx(LuUnlink, {}),
            onClick: () => unAssignHandler(),
            intent: 'danger',
            title: 'Unassign range',
            visible: isAssignmentActive,
        },
        {
            icon: _jsx(PiTextTBold, {}),
            onClick: () => addNewAssignmentLabel(range.id),
            intent: 'success',
            title: 'Add assignment label',
            visible: !assignment,
        },
        {
            icon: _jsx(PiTextTSlashBold, {}),
            onClick: removeAssignemntlabel,
            intent: 'danger',
            title: 'Remove assignment label',
            visible: !!assignment,
        },
    ];
    return (_jsx("g", { "data-testid": "range", style: { outline: 'none' }, onMouseEnter: mouseEnterHandler, onMouseLeave: mouseLeaveHandler, ...(!assignmentRange.isActive && { css: style }), children: _jsx(ActionsButtonsPopover, { targetTagName: "g", ...(assignmentRange.isActive && { isOpen: true }), buttons: actionsButtons, children: _jsx(ResizerWithScale, { from: from, to: to, onEnd: handleOnStopResizing, disabled: !isResizingActive, children: ({ x1, x2 }, isActive) => {
                    const width = x2 - x1;
                    return (_jsxs("g", { children: [isAssigned && !isHighlighted && !isActive && (_jsx("rect", { width: width, height: "10px", fill: "#ffd700", opacity: "0.35", "data-no-export": "true" })), _jsx("rect", { width: width, height: "100%", fill: isHighlighted || isActive ? '#ff6f0057' : 'transparent', "data-no-export": "true" }), _jsx(AssignmentLabel, { range: range, width: width }), _jsx(Atoms, { range: range, x: width / 2 }), showIntegralsValues && (_jsx(IntegralIndicator, { value: integration, format: relativeFormat, width: width, opacity: isNotSignal ? 0.5 : 1 }))] }));
                } }) }) }, id));
}
export default Range;
